import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import normalizeStringValue from '@commons/utils/normalizeStringValue';

const getSheetConfiguration = () => ({
  SUPPLIER_PRODUCT: {
    id: 'supplierProducts',
    name: i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SHEET_NAME'),
  },
  CATEGORIES: {
    id: 'categories',
    name: i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.CATEGORY'),
  },
  SUB_CATEGORIES: {
    id: 'subCategories',
    name: i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SUB_CATEGORIES_NAME'),
  },
  SUPPLIERS: {
    id: 'suppliers',
    name: i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SUPPLIER'),
  },
  INGREDIENT: {
    id: 'ingredients',
    name: i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.INGREDIENT'),
  },
});

const getNormalizedColumnIds = () => ({
  ID: { key: 'id', normalizedKey: 'id' },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.NAME'),
    ),
  },
  SKU: {
    key: 'sku',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SKU'),
    ),
    // Avoid considering SKU as number
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, '').toString(),
  },
  EAN: {
    key: 'ean',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.EAN'),
    ),
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, '').toString() || null,
  },
  SUPPLIER: {
    key: 'supplierName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SUPPLIER'),
    ),
  },
  CATEGORY: {
    key: 'category',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.CATEGORY'),
    ),
  },
  SUB_CATEGORY: {
    key: 'subCategory',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SUB_CATEGORY'),
    ),
  },
  SHELF_LIFE: {
    key: 'shelfLife',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SHELF_LIFE'),
    ),
  },
  PRICE: {
    key: 'price',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PRICE'),
    ),
  },
  VAT_RATE: {
    key: 'vatRate',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.VAT_RATE'),
    ),
  },
  INVOICE_PACKAGING: {
    key: 'invoicePackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.INVOICE_PACKAGING'),
    ),
  },
  SCHEDULED_PRICE_VALUE: {
    key: 'scheduledPriceValue',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SCHEDULED_PRICE_VALUE'),
    ),
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, null),
  },
  SCHEDULED_PRICE_DATE: {
    key: 'scheduledPriceStartDate',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SCHEDULED_PRICE_START_DATE'),
    ),
    mappingFn: (row, normalizedKey) => {
      const scheduledStartDate = get(row, normalizedKey, null);
      const isScheduledStartDateValid = moment(
        scheduledStartDate,
        [DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR, DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR],
        true,
      ).isValid();

      // Adding one hour because xlsx parsing remove approximately 30 minutes from a date and there's no real fix for it
      // except this quickfix, see issue: https://github.com/SheetJS/sheetjs/issues/1565
      // We do not take timezone into account so that the timezone stays at DD-MM-YYYY-00-00-00
      return isScheduledStartDateValid
        ? moment(scheduledStartDate, [
            DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
            DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR,
          ])
            .utc(true)
            .add(1, 'h')
            .startOf('day')
            .format()
        : null;
    },
  },
  LOSS: {
    key: 'loss',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.LOSS'),
    ),
  },
  PRICE_EDITABLE_AT_RECEPTION: {
    key: 'priceEditableAtReception',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PRICE_EDITABLE_AT_RECEPTION'),
    ),
    mappingFn: (row, normalizedKey) =>
      get(row, normalizedKey, '').toUpperCase() === i18next.t('GENERAL.YES').toUpperCase(),
  },
  CONVERSIONS: {
    key: 'conversions',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.CONVERSIONS'),
    ),
  },
  ENTITY: {
    key: 'associatedIngredient',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.ENTITY'),
    ),
  },
  ORDER_PACKAGING: {
    key: 'orderPackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.ORDER_PACKAGING'),
    ),
  },
  DELIVERY_PACKAGING: {
    key: 'receptionPackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.DELIVERY_PACKAGING'),
    ),
  },
  PACKAGE_WEIGHT: {
    key: 'packagingWeight',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGE_WEIGHT'),
    ),
  },
  PACKAGING_NAME_1: {
    key: 'packagingName1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_1'),
    ),
  },
  PACKAGING_UNIT_1: {
    key: 'packagingUnit1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_1'),
    ),
  },
  PACKAGING_QUANTITY_1: {
    key: 'packagingQuantity1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_1'),
    ),
  },
  PACKAGING_NAME_2: {
    key: 'packagingName2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_2'),
    ),
  },
  PACKAGING_UNIT_2: {
    key: 'packagingUnit2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_2'),
    ),
  },
  PACKAGING_QUANTITY_2: {
    key: 'packagingQuantity2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_2'),
    ),
  },
  PACKAGING_NAME_3: {
    key: 'packagingName3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_3'),
    ),
  },
  PACKAGING_UNIT_3: {
    key: 'packagingUnit3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_3'),
    ),
  },
  PACKAGING_QUANTITY_3: {
    key: 'packagingQuantity3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_3'),
    ),
  },
  MANUFACTURER_NAME: {
    key: 'manufacturerName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.MANUFACTURER_NAME'),
    ),
  },
  DETAILED_COMPOSITION: {
    key: 'detailedComposition',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.DETAILED_COMPOSITION'),
    ),
  },

  ENERGY: {
    key: 'energy',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.ENERGY_KCAL_PER_100G'),
    ),
  },
  FAT: {
    key: 'fat',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.FAT_PER_100G'),
    ),
  },
  SATURATED_FAT: {
    key: 'saturatedFat',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SATURATED_FAT_PER_100G'),
    ),
  },
  CARBOHYDRATES: {
    key: 'carbohydrates',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.CARBOHYDRATES_PER_100G'),
    ),
  },
  SUGAR: {
    key: 'sugar',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SUGARS_PER_100G'),
    ),
  },
  FIBER: {
    key: 'fiber',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.DIETARY_FIBER_PER_100G'),
    ),
  },
  PROTEIN: {
    key: 'protein',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PROTEINS_PER_100G'),
    ),
  },
  SALT: {
    key: 'salt',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.SALT_PER_100G'),
    ),
  },
});

// For dataModel
const COLUMN_IDS = {
  ID: { key: 'id', normalizedKey: 'id' },
  NAME: { key: 'name', normalizedKey: 'nom' },
  SKU: {
    key: 'sku',
    normalizedKey: 'sku',
    // Avoid considering SKU as number
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, '').toString(),
  },
  EAN: {
    key: 'ean',
    normalizedKey: 'code ean',
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, '').toString() || null,
  },
  SUPPLIER: { key: 'supplierName', normalizedKey: 'fournisseur' },
  CATEGORY: { key: 'category', normalizedKey: 'categorie' },
  SUB_CATEGORY: { key: 'subCategory', normalizedKey: 'sous-categorie' },
  SHELF_LIFE: { key: 'shelfLife', normalizedKey: 'dlc | ddm (en jours)' },
  PRICE: { key: 'price', normalizedKey: 'prix ht' },
  VAT_RATE: { key: 'vatRate', normalizedKey: 'tva (%)' },
  INVOICE_PACKAGING: {
    key: 'invoicePackagingName',
    normalizedKey: 'conditionnement de facturation',
  },
  ORDER_PACKAGING: {
    key: 'orderPackagingName',
    normalizedKey: 'conditionnement de commande',
  },
  DELIVERY_PACKAGING: {
    key: 'receptionPackagingName',
    normalizedKey: 'conditionnement de reception',
  },
  SCHEDULED_PRICE_VALUE: {
    key: 'scheduledPriceValue',
    normalizedKey: 'prix ht a programmer',
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, null),
  },
  SCHEDULED_PRICE_DATE: {
    key: 'scheduledPriceStartDate',
    normalizedKey: 'date de changement de prix',
    mappingFn: (row, normalizedKey) => {
      const scheduledStartDate = get(row, normalizedKey, null);
      const isScheduledStartDateValid = moment(
        scheduledStartDate,
        [DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR, DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR],
        true,
      ).isValid();

      // Adding one hour because xlsx parsing remove approximately 30 minutes from a date and there's no real fix for it
      // except this quickfix, see issue: https://github.com/SheetJS/sheetjs/issues/1565
      // We do not take timezone into account so that the timezone stays at DD-MM-YYYY-00-00-00
      return isScheduledStartDateValid
        ? moment(scheduledStartDate, [
            DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
            DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR,
          ])
            .utc(true)
            .add(1, 'h')
            .startOf('day')
            .format()
        : null;
    },
  },
  LOSS: {
    key: 'loss',
    normalizedKey: 'pertes (%)',
  },
  PRICE_EDITABLE_AT_RECEPTION: {
    key: 'priceEditableAtReception',
    normalizedKey: 'prix modifiable a reception',
    mappingFn: (row, normalizedKey) => get(row, normalizedKey) === 'OUI',
  },
  ENTITY: {
    key: 'associatedIngredient',
    normalizedKey: 'ingredient associe',
  },
  PACKAGING_NAME_1: { key: 'packagingName1', normalizedKey: 'conditionnement 1' },
  PACKAGING_UNIT_1: {
    key: 'packagingUnit1',
    normalizedKey: 'unite conditionnement 1',
  },
  PACKAGING_QUANTITY_1: {
    key: 'packagingQuantity1',
    normalizedKey: 'quantite conditionnement 1',
  },
  PACKAGING_NAME_2: { key: 'packagingName2', normalizedKey: 'conditionnement 2' },
  PACKAGING_UNIT_2: {
    key: 'packagingUnit2',
    normalizedKey: 'unite conditionnement 2',
  },
  PACKAGING_QUANTITY_2: {
    key: 'packagingQuantity2',
    normalizedKey: 'quantite conditionnement 2',
  },
  PACKAGING_NAME_3: { key: 'packagingName3', normalizedKey: 'conditionnement 3' },
  PACKAGING_UNIT_3: {
    key: 'packagingUnit3',
    normalizedKey: 'unite conditionnement 3',
  },
  PACKAGING_QUANTITY_3: {
    key: 'packagingQuantity3',
    normalizedKey: 'quantite conditionnement 3',
  },
  MANUFACTURER_NAME: { key: 'manufacturerName', normalizedKey: 'nom du fabricant' },
  PACKAGE_WEIGHT: { key: 'packagingWeight', normalizedKey: 'poids conditionnement commande (kg)' },
  CURRENCY: { key: 'currency', normalizedKey: 'devise' },
};

export default {
  getSheetConfiguration,
  getNormalizedColumnIds,
  COLUMN_IDS,
};
